import { useState, useCallback, useRef } from 'react';
import useUserStore from '../../../common/src/stores/useUserStore';
import { usePageContext } from './usePageContext';
import useBrand from '../../../common/src/hooks/useBrand';

export interface ChartSpec {
  type: 'line' | 'bar' | 'area';
  title: string;
  categories: string[];
  series: { name: string; data: (number | null)[] }[];
  unit?: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  /** Trail of data queries the agent ran while building this reply. */
  steps?: string[];
  /** Charts the agent rendered for this reply. */
  charts?: ChartSpec[];
}

const WORKERS_URL = import.meta.env.VITE_WORKERS_URL || 'http://localhost:3003';

export function useVictoriaChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // What the agent is doing right now ("Consultando FOB por etiqueta...");
  // null when idle or streaming text.
  const [activity, setActivity] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const pageContext = usePageContext();
  // Marca fijada al cargar (dominio o ?brand=), igual que el resto de la app.
  const { key: brandKey } = useBrand();

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading) return;

      // Add user message
      const userMsg: ChatMessage = {
        id: Date.now().toString(),
        role: 'user',
        content: trimmed,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setIsLoading(true);

      // Get fresh token
      const session = useUserStore.getState().session;
      const token = session?.access_token;
      if (!token) {
        const errorMsg: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: 'Tu sesion expiro. Recarga la pagina e intenta de nuevo.',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMsg]);
        setIsLoading(false);
        return;
      }

      // Build messages history for API (only role + content)
      const apiMessages = [...messages, userMsg].map((m) => ({
        role: m.role,
        content: m.content,
      }));

      // Create assistant placeholder for streaming
      const assistantId = (Date.now() + 1).toString();
      const assistantMsg: ChatMessage = {
        id: assistantId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMsg]);

      // Abort previous request if any
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        };

        const response = await fetch(`${WORKERS_URL}/web-chat/stream`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            messages: apiMessages,
            context: pageContext,
            userName: useUserStore.getState().userProfile?.first_name || null,
            userId: useUserStore.getState().userProfile?.id || null,
            // El worker se presenta como CherryMarket IA o FruitMarkets IA.
            brand: brandKey,
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        // Read SSE stream
        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Parse SSE events from buffer
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // keep incomplete line in buffer

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const jsonStr = line.slice(6);
            try {
              const event = JSON.parse(jsonStr);
              if (event.type === 'text') {
                setActivity(null);
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantId
                      ? { ...m, content: m.content + event.content }
                      : m
                  )
                );
              } else if (event.type === 'chart') {
                setActivity(null);
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantId
                      ? { ...m, charts: [...(m.charts ?? []), event.spec] }
                      : m
                  )
                );
              } else if (event.type === 'status') {
                setActivity(event.content);
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantId &&
                    m.steps?.[m.steps.length - 1] !== event.content
                      ? { ...m, steps: [...(m.steps ?? []), event.content] }
                      : m
                  )
                );
              } else if (event.type === 'error') {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantId
                      ? {
                          ...m,
                          content:
                            event.content || 'Error procesando tu consulta.',
                        }
                      : m
                  )
                );
              }
            } catch {
              // ignore malformed JSON
            }
          }
        }
      } catch (err: any) {
        if (err.name === 'AbortError') return;

        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  content:
                    m.content ||
                    'No pude conectar con el servicio. Intenta de nuevo en unos segundos.',
                }
              : m
          )
        );
      } finally {
        setIsLoading(false);
        setActivity(null);
        abortRef.current = null;
      }
    },
    [messages, isLoading, pageContext, brandKey]
  );

  const clearChat = useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
    setIsLoading(false);
    setActivity(null);
  }, []);

  return { messages, isLoading, activity, sendMessage, clearChat };
}
